import { useDisclosure } from '@chakra-ui/hooks';
import React, { useState } from 'react';

import {
  CmBox,
  CmButton,
  CmModal,
  CmModalBody,
  CmModalFooter,
  CmText,
} from '@/components/uiParts';
import { errorHandler } from '@/lib/ErrorHandler';
import { useUpdateIntegrationReadyDeliveryStoreOrderManager } from '@/services/StoreManagement/IntegrationReadyDeliveryStore/hooks/useUpdateIntegrationReadyDeliveryStoreOrderManager';
import { UberStoreListResponse } from '@/services/StoreManagement/UberStores/hooks';

interface Props {
  uberStore: UberStoreListResponse;
  onUpdated: (storeId: string, isOrderManager: boolean) => void;
}

// 連携済（linked_status !== null）の Uber Eats 店舗 1 件に対して、
// 注文連携の有効/無効を即時切替する行内アクション。
// PATCH /integration-ready-delivery-stores/order-manager を 1 リクエスト送信する。
export const OrderManagerToggleAction: React.VFC<Props> = ({
  uberStore,
  onUpdated,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 切替後の値（現在 true → false に、現在 false → true にする）
  const nextIsOrderManager = !uberStore.is_order_manager;

  // 「注文連携を無効化」する操作のときのみ警告が必要
  const isDisablingOrder = uberStore.is_order_manager === true;

  const buttonLabel = isDisablingOrder
    ? '注文連携を無効化'
    : '注文連携を有効化';

  const modalTitle = isDisablingOrder
    ? '注文連携を無効化しますか？'
    : '注文連携を有効化しますか？';

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await useUpdateIntegrationReadyDeliveryStoreOrderManager({
        external_store_id: uberStore.id,
        delivery_service_id: 1,
        is_order_manager: nextIsOrderManager,
      });
      onUpdated(uberStore.id, nextIsOrderManager);
      onClose();
    } catch (error) {
      // @ts-ignore TS2322
      errorHandler({ error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/*
       // @ts-ignore TS2590 */}
      <CmButton
        size="sm"
        onClick={onOpen}
        colorScheme={isDisablingOrder ? 'red' : 'teal'}
        variant="outline"
      >
        {buttonLabel}
      </CmButton>

      <CmModal
        title={modalTitle}
        headerSize="md"
        size="2xl"
        {...{ isOpen, onClose }}
      >
        <CmModalBody>
          <CmText fontSize="lg" mb={2}>
            対象店舗: {uberStore.name}
          </CmText>
          {isDisablingOrder ? (
            <CmBox mt={4} p={3} bg="red.50" borderRadius="md">
              <CmText fontSize="sm" color="red.800">
                実行すると注文連携が解除されます。Uber Eats からの注文は Camel
                に取り込まれなくなりますのでご注意ください。
              </CmText>
            </CmBox>
          ) : (
            <CmBox mt={4} p={3} bg="orange.50" borderRadius="md">
              <CmText fontSize="sm" color="orange.800">
                ※ 注文管理（Order Manager）を「有効」に設定すると、後勝ちで
                Order Manager 権限がこのアカウントに移ります。他のシステムが既に
                Order Manager
                を保持している場合、そちらの権限が失われますのでご注意ください。
              </CmText>
            </CmBox>
          )}
        </CmModalBody>
        <CmModalFooter>
          <CmButton variant="outline" mr={4} onClick={onClose}>
            キャンセル
          </CmButton>
          <CmButton
            width={100}
            colorScheme={isDisablingOrder ? 'red' : 'teal'}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            実行する
          </CmButton>
        </CmModalFooter>
      </CmModal>
    </>
  );
};
